-- | This module implements a "dynamic" widget system which uses a sort of
-- | dynamic dispatch to create an object which could contain anything that
-- | implements the `FormWidget` class.
-- |
-- | This was necessary to prevent an inherent circular dependency between the
-- | sum type containing all possible form widgets and `SubForm` and related
-- | widgets, which by nature have to depend on the widget type, as they
-- | contain a list of sub-widgets. Since purescript does not allow circular
-- | imports, the sum type and anything that contains it had to be in the same
-- | file, leading to one massive file with many different unrelated things in
-- | it.
-- |
-- | By doing dynamic dispatch, we remove the need for the sum type altogether,
-- | which removes the circular dependency. As a nice bonus, this also removes
-- | the need to pattern match on a sum type with many differnt possible
-- | variants in order to dispatch calls to `toWidget`, `getData`, etc. to the
-- | correct code.
-- |
-- | The implementation of this module is fairly complex, but the interface is
-- | dead simple. It exposes a `DynFormWidget` type, which is essentially a
-- | black box that can contain anything that implements `FormWidget`, and a
-- | `wrapFormWidget` function which can safely turn anything that implements
-- | `FormWidget` into a `DynFormWidget`.
module Widgets.Form.Dynamic (DynFormWidget(), wrapFormWidget) where

import Common

import Concur.Core (Widget)
import Concur.React (HTML)
import Data.Argonaut (Json)
import GraphQL.Shape (QueryField)
import Unsafe.Coerce (unsafeCoerce)
import Widgets.Form.Class (class FormWidget, FormContext, FormWidgetResult, asField, getData, getId, isLeaf, fieldPaths, setData, toWidget)


-- | This is a type alias which could contain *any* type at all.
-- |
-- | This is an internal detail of this module and should never be dealt with directly.
foreign import data Anything :: Type


-- | This is a dynamic form widget which can dynamically contain any type that
-- | implements `FormWidget`.
-- |
-- | This type implements those type classes and wraps their functions such
-- | that the internal dynamic type wrapped by this is cast back to its correct
-- | concrete type before calling the function.
-- |
-- | Since all type-casting happens internally, this type's external interface
-- | should be completely type-safe.
-- |
-- | Instances of this type should *never* be created directly, but instead by
-- | calling `wrapFormWidget`.
data DynFormWidget = DynFormWidget
    { inner :: Anything
    -- Wrapped functions. These take `Anything` instead of the concrete type,
    -- but are constructed by `wrapFormWidget` such that they always cast from
    -- whatever type `inner` actually has.
    , getId :: DynFormWidget -> String
    , isLeaf :: DynFormWidget -> Boolean
    , asField :: DynFormWidget -> Maybe QueryField
    , toWidget :: FormContext -> DynFormWidget -> Widget HTML (FormWidgetResult DynFormWidget)
    -- This will return a null json value for widgets that do not implement
    -- `FormWidget` (for example, label, which has no data).
    , getData :: FormContext -> DynFormWidget -> Json
    -- This will return an error for types which do not implement `FormWidget`.
    , setData :: FormContext -> DynFormWidget -> Json -> Either ErrorWrapper DynFormWidget
    , fieldPaths :: FormContext -> DynFormWidget -> Array String
    -- Wrap `show` too for debugging. It will behave as if show was called on
    -- `inner`.
    , show :: DynFormWidget -> String
    }


-- | This function takes any type which implements `FormWidget`, and wraps it in
-- | a `DynFormWidget` type for "dynamic dispatch".
-- |
-- | This is where the actual "magic" happens which ensures type safety. The
-- | widget is only typecast within this function, and always to the correct
-- | type, as guaranteed by the type variable `w`.
wrapFormWidget :: forall w. Show w => FormWidget w => w -> DynFormWidget
wrapFormWidget widget = DynFormWidget
    -- Actual implementation of this is done with `unsafeCoerce`. All calls to
    -- said function must be within the context of this function, where the
    -- type `w` (the widget's actual concrete type) is known by the
    -- type-checker at compile time. Unless some external code somehow changes
    -- `inner` to something else, this guarantees that inner will always be
    -- cast back from `Anything` to the same type it was cast from (that is,
    -- whatever type `w` is in this function).
    { inner: toAny widget
    -- Wrap all the functions.
    , getId: getId <<< fromDFW
    , isLeaf: isLeaf <<< fromDFW
    , asField: asField <<< fromDFW
    , toWidget: wrappedToWidget
    , getData: \fc -> getData fc <<< fromDFW
    , setData: wrappedSetData
    , fieldPaths: \fc -> fieldPaths fc <<< fromDFW
    , show: show <<< fromDFW
    }
  where
    -- For `toWidget` we must un-wrap the widget state passed in and then wrap
    -- the return from the widget back into `DynFormWidget`.
    --
    -- We also handle debouncing here.
    wrappedToWidget :: FormContext -> DynFormWidget -> Widget HTML (FormWidgetResult DynFormWidget)
    wrappedToWidget fc dfw | isLeaf dfw =
        (updateDFW dfw <$> _) <$> (toWidget fc $ fromDFW dfw)
    wrappedToWidget fc dfw = (updateDFW dfw <$> _) <$> toWidget fc (fromDFW dfw)
    -- Same applies to `setData`.
    wrappedSetData :: FormContext -> DynFormWidget -> Json -> Either ErrorWrapper DynFormWidget
    wrappedSetData fc dfw json = updateDFW dfw <$> setData fc (fromDFW dfw) json

    -- Here we wrap `unsafeCoerce` so that it always casts to whatever type `w`
    -- is. This guarantees that we are casting to the correct type.
    fromAny :: Anything -> w
    fromAny = unsafeCoerce
    -- And do the same for the other direction.
    toAny :: w -> Anything
    toAny = unsafeCoerce
    -- Utility which unwraps `DynFormWidget` and casts.
    fromDFW :: DynFormWidget -> w
    fromDFW (DynFormWidget { inner }) = fromAny inner
    -- Takes an old `DynFormWidget` and replaces `inner` with the given value.
    updateDFW :: DynFormWidget -> w -> DynFormWidget
    updateDFW (DynFormWidget dfw) new = DynFormWidget $ dfw { inner = toAny new }


-- Now some typeclass instances to make dynamic widgets behave exactly like any
-- other widget. These just call the wrapped functions stored in the record.

instance dynWidgetShow :: Show DynFormWidget where
    show outer@(DynFormWidget dfw) = dfw.show outer

instance dynFormWidget :: FormWidget DynFormWidget where
    getId outer@(DynFormWidget dfw) = dfw.getId outer
    isLeaf outer@(DynFormWidget dfw) = dfw.isLeaf outer
    asField outer@(DynFormWidget dfw) = dfw.asField outer

    toWidget fc outer@(DynFormWidget dfw) = dfw.toWidget fc outer

    getData fc outer@(DynFormWidget dfw) = dfw.getData fc outer
    setData fc outer@(DynFormWidget dfw) json = dfw.setData fc outer json
    fieldPaths fc outer@(DynFormWidget dfw) = dfw.fieldPaths fc outer
